import type { DemoData, EnrichedPart, TaxonomyProductMapping } from "../domain/types";

export const ERP_DESCRIPTION_LIMIT = 80;

export type TransformationPreviewStatus = "ready" | "review_required";

export type SourcePartPayload = {
    part_id: string;
    manufacturer: string;
    mpn: string;
    category: string;
    taxonomy_path: string;
    lifecycle_status: string;
    package_type: string | null;
    pin_count: number | null;
    voltage_range: string | null;
    capacitance: string | null;
    resistance: string | null;
    tolerance: string | null;
    rohs_status: string | null;
    confidence_score: number;
};

export type ErpItemPayload = {
    item_id: string;
    manufacturer: string;
    mpn: string;
    product_group: string;
    product_line: string;
    description: string;
    description_limit: number;
    lifecycle_status: string;
    publish_action: "publish" | "hold_for_review";
};

export type CrmProductPayload = {
    product_id: string;
    name: string;
    family: string;
    manufacturer: string;
    mpn: string;
    active: boolean;
    compliance_status: string;
    confidence_score: number;
};

export type TransformationPreview = {
    part: EnrichedPart;
    inputPayload: SourcePartPayload;
    mapping: TaxonomyProductMapping | null;
    status: TransformationPreviewStatus;
    reasonCode: string;
    reasonText: string;
    crmProductName: string;
    erpDescription: string;
    fullErpDescription: string;
    erpDescriptionWasTruncated: boolean;
    droppedDescriptionFragments: string[];
    erpPayload: ErpItemPayload;
    crmPayload: CrmProductPayload;
};

export const formatVoltage = (part: EnrichedPart): string | null => {
    if (part.voltageMin == null || part.voltageMax == null) return null;
    return `${part.voltageMin}-${part.voltageMax}V`;
};

export const formatCapacitance = (valuePf: number | null): string | null => {
    if (valuePf == null) return null;
    if (valuePf >= 1000000) return `${valuePf / 1000000}uF`;
    if (valuePf >= 1000) return `${valuePf / 1000}nF`;
    return `${valuePf}pF`;
};

export const formatResistance = (valueOhms: number | null): string | null => {
    if (valueOhms == null) return null;
    if (valueOhms >= 1000) return `${valueOhms / 1000}kOhm`;
    return `${valueOhms}Ohm`;
};

export const formatTolerance = (valuePct: number | null): string | null =>
    valuePct == null ? null : `${valuePct}%`;

export const compactMpn = (mpn: string): string => mpn.trim().replace(/\s+/g, " ");

export const buildCrmProductName = (part: EnrichedPart): string => {
    const attributes = [
        formatCapacitance(part.capacitancePf),
        formatResistance(part.resistanceOhms),
        formatVoltage(part),
        part.packageType,
        formatTolerance(part.tolerancePct),
    ].filter(Boolean);

    return `${part.manufacturerName} ${compactMpn(part.mpn)}${attributes.length > 0 ? ` - ${attributes.join(" ")}` : ""}`;
};

const fitDescription = (baseFragments: string[], optionalFragments: string[], limit: number) => {
    const used = [...baseFragments];
    const dropped: string[] = [];

    optionalFragments.forEach((fragment) => {
        const candidate = [...used, fragment].join(" | ");
        if (candidate.length <= limit) {
            used.push(fragment);
        } else {
            dropped.push(fragment);
        }
    });

    const description = used.join(" | ");
    if (description.length <= limit) {
        return { description, dropped };
    }

    return {
        description: `${description.slice(0, Math.max(0, limit - 3))}...`,
        dropped,
    };
};

export const buildErpDescription = (part: EnrichedPart, limit = ERP_DESCRIPTION_LIMIT) => {
    const baseFragments = [part.manufacturerName, compactMpn(part.mpn)];
    const optionalFragments = [
        part.category,
        formatVoltage(part),
        part.packageType,
        part.pinCount == null ? null : `${part.pinCount} pins`,
        formatCapacitance(part.capacitancePf),
        formatResistance(part.resistanceOhms),
        formatTolerance(part.tolerancePct),
        part.rohsStatus == null ? null : `RoHS ${part.rohsStatus}`,
        `Lifecycle ${part.lifecycleStatus}`,
    ].filter((fragment): fragment is string => Boolean(fragment));
    const fullDescription = [...baseFragments, ...optionalFragments].join(" | ");
    const { description, dropped } = fitDescription(baseFragments, optionalFragments, limit);

    return {
        description,
        fullDescription,
        wasTruncated: description !== fullDescription,
        droppedFragments: dropped,
    };
};

const buildInputPayload = (part: EnrichedPart): SourcePartPayload => ({
    part_id: part.partId,
    manufacturer: part.manufacturerName,
    mpn: part.mpn,
    category: part.category,
    taxonomy_path: part.taxonomyPath,
    lifecycle_status: part.lifecycleStatus,
    package_type: part.packageType,
    pin_count: part.pinCount,
    voltage_range: formatVoltage(part),
    capacitance: formatCapacitance(part.capacitancePf),
    resistance: formatResistance(part.resistanceOhms),
    tolerance: formatTolerance(part.tolerancePct),
    rohs_status: part.rohsStatus,
    confidence_score: part.confidenceScore,
});

const previewStatusForPart = (part: EnrichedPart, mapping: TaxonomyProductMapping | null): Pick<TransformationPreview, "status" | "reasonCode" | "reasonText"> => {
    if (!mapping) {
        return {
            status: "review_required",
            reasonCode: "MISSING_TAXONOMY_MAPPING",
            reasonText: "Source taxonomy is not mapped to downstream product group, line, and family values.",
        };
    }

    if (part.hasMandatoryAttributeGap) {
        return {
            status: "review_required",
            reasonCode: "DESCRIPTION_INPUT_INCOMPLETE",
            reasonText: "Required attributes are missing, so downstream description and product-name outputs are held.",
        };
    }

    if (part.hasMpnFormatIssue) {
        return {
            status: "review_required",
            reasonCode: "PRODUCT_NAME_INPUT_SUSPECT",
            reasonText: "MPN formatting is suspicious, so text merge output is routed for review.",
        };
    }

    return {
        status: "ready",
        reasonCode: "TRANSFORM_READY",
        reasonText: "Taxonomy, description, and product-name transformations are ready for downstream publish.",
    };
};

export const buildTransformationPreview = (data: DemoData, part: EnrichedPart): TransformationPreview => {
    const mapping = data.taxonomyMappings.find((candidate) => candidate.taxonomyPath === part.taxonomyPath) ?? null;
    const productName = buildCrmProductName(part);
    const description = buildErpDescription(part);
    const status = previewStatusForPart(part, mapping);
    const publishAction = status.status === "ready" ? "publish" : "hold_for_review";

    return {
        part,
        inputPayload: buildInputPayload(part),
        mapping,
        ...status,
        crmProductName: productName,
        erpDescription: description.description,
        fullErpDescription: description.fullDescription,
        erpDescriptionWasTruncated: description.wasTruncated,
        droppedDescriptionFragments: description.droppedFragments,
        erpPayload: {
            item_id: `ERP-PREVIEW-${part.partId.replace("PART-", "")}`,
            manufacturer: part.manufacturerName,
            mpn: compactMpn(part.mpn),
            product_group: mapping?.erpProductGroup ?? "UNMAPPED",
            product_line: mapping?.erpProductLine ?? "Review Required",
            description: description.description,
            description_limit: ERP_DESCRIPTION_LIMIT,
            lifecycle_status: part.lifecycleStatus,
            publish_action: publishAction,
        },
        crmPayload: {
            product_id: `CRM-PREVIEW-${part.partId.replace("PART-", "")}`,
            name: productName,
            family: mapping?.crmFamily ?? "Review Required",
            manufacturer: part.manufacturerName,
            mpn: compactMpn(part.mpn),
            active: status.status === "ready" && part.lifecycleStatus !== "EOL" && part.lifecycleStatus !== "NRND",
            compliance_status: part.rohsStatus ?? "Unknown",
            confidence_score: part.confidenceScore,
        },
    };
};

const findPart = (parts: EnrichedPart[], predicate: (part: EnrichedPart) => boolean): EnrichedPart | undefined => parts.find(predicate);

export const buildTransformationPreviews = (data: DemoData, maxRows = 8): TransformationPreview[] => {
    const mappedTaxonomyPaths = new Set(data.taxonomyMappings.map((mapping) => mapping.taxonomyPath));
    const selected = [
        findPart(data.parts, (part) => mappedTaxonomyPaths.has(part.taxonomyPath) && !part.hasMandatoryAttributeGap && !part.hasMpnFormatIssue),
        findPart(data.parts, (part) => !mappedTaxonomyPaths.has(part.taxonomyPath)),
        findPart(data.parts, (part) => part.hasMandatoryAttributeGap),
        findPart(data.parts, (part) => part.hasMpnFormatIssue),
        findPart(data.parts, (part) => part.lifecycleStatus === "EOL" || part.lifecycleStatus === "NRND"),
    ].filter((part): part is EnrichedPart => Boolean(part));
    const uniqueSelected = Array.from(new Map(selected.map((part) => [part.partId, part])).values());
    const fillRows = data.parts.filter((part) => !uniqueSelected.some((selectedPart) => selectedPart.partId === part.partId));

    return [...uniqueSelected, ...fillRows].slice(0, maxRows).map((part) => buildTransformationPreview(data, part));
};
